import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle, XCircle, IdentificationBadge } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const LABELS = {
  code: "Employee ID",
  name: "Name",
  role: "Role",
  department: "Department",
  hourly_rate: "Hourly rate",
  machine_specialization: "Machine",
  email: "Email",
  mobile: "Mobile",
};

export default function TeamChangeApprovals({ canDecide }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["team-change-requests"],
    queryFn: async () => (await api.get("/team-change-requests")).data,
    refetchInterval: 60000,
  });

  const rows = data || [];
  if (rows.length === 0) return null;

  const act = async (rid, status) => {
    try {
      await api.post(`/team-change-requests/${rid}/approval`, { status });
      toast.success(`Team member change ${status.toLowerCase()}`);
      qc.invalidateQueries({ queryKey: ["team-change-requests"] });
      qc.invalidateQueries({ queryKey: ["entity"] });
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="border-blue-200 bg-blue-50/50 shadow-none" data-testid="team-change-approvals">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-blue-100">
        <IdentificationBadge className="h-5 w-5 text-blue-600" weight="fill" />
        <h3 className="font-semibold text-blue-900">
          Team Member Changes — {rows.length} awaiting approval
        </h3>
      </div>
      <ul className="divide-y divide-blue-100">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-5 py-3"
            data-testid={`team-change-request-${r.id}`}>
            <span className="text-sm min-w-0">
              <span className="font-mono text-xs text-slate-500">{r.team_code}</span>{" "}
              <span className="text-slate-900 font-medium">{r.team_name}</span>
              <span className="block text-xs text-slate-600 mt-0.5">
                {Object.entries(r.changes || {}).map(([k, v]) => (
                  <span key={k} className="mr-3">
                    {LABELS[k] || k}: <span className="text-slate-400">{String(v.from ?? "—")}</span>
                    {" → "}
                    <span className="font-semibold text-blue-700">{String(v.to ?? "—")}</span>
                  </span>
                ))}
              </span>
              <span className="block text-xs text-slate-500 mt-0.5">Requested by {r.requested_by}</span>
            </span>
            {canDecide ? (
              <span className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" className="h-7 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  onClick={() => act(r.id, "Approved")} data-testid={`team-change-approve-${r.id}`}>
                  <CheckCircle className="h-4 w-4 mr-1" weight="fill" /> Approve
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-red-700 border-red-200 hover:bg-red-50"
                  onClick={() => act(r.id, "Rejected")} data-testid={`team-change-reject-${r.id}`}>
                  <XCircle className="h-4 w-4 mr-1" weight="fill" /> Reject
                </Button>
              </span>
            ) : (
              <span className="text-xs text-slate-500 shrink-0">Waiting for a Manager to approve</span>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
